/**
 * Financial Parameters Admin Page
 * 
 * Manage versioned financial parameters for the Exposure Engine:
 * - Parameter Sets with approval workflow
 * - Jurisdiction profiles
 * - Base penalties, claim costs, unit costs
 * - Substitution multipliers (SDC)
 * - Audit trail
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 

  Settings, Plus, Edit3, Check, X, Clock, Archive, AlertCircle,
  ChevronRight, ChevronDown, RefreshCw, Shield, Globe, DollarSign,
  Scale, Truck, History, FileText, Play, Pause, Lock, Unlock,
  AlertTriangle, TrendingUp, Database, Layers, Users
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  APPROVED: { label: 'Approved', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  ACTIVE: { label: 'Active', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  RETIRED: { label: 'Retired', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
};

// Tab configuration
const TABS = [
  { id: 'overview', label: 'Overview', icon: Layers },
  { id: 'jurisdictions', label: 'Jurisdictions', icon: Globe },
  { id: 'penalties', label: 'Base Penalties', icon: Scale },
  { id: 'claims', label: 'Claim Costs', icon: DollarSign },
  { id: 'unitcosts', label: 'Unit Costs', icon: Settings },
  { id: 'sdc', label: 'SDC Multipliers', icon: Truck },
  { id: 'audit', label: 'Audit Trail', icon: History }
];

const FinancialParametersAdmin = () => {
  // State
  const [parameterSets, setParameterSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState(null);
  const [selectedSet, setSelectedSet] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [auditLog, setAuditLog] = useState([]);
  const [stats, setStats] = useState({});
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState(null);

  // Fetch parameter sets
  const fetchParameterSets = useCallback(async () => {
    try {
      const data = await apiFetch(`/spine/v1/fin/admin/parameter-sets`);
      if (data && !data._error) {
        setParameterSets(data);
        if (data.length > 0 && !selectedSetId) {
          setSelectedSetId(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching parameter sets:', err);
    }
  }, [selectedSetId]);

  // Fetch selected set details
  const fetchSetDetails = useCallback(async () => {
    if (!selectedSetId) return;
    
    setLoading(true);
    try {
      const data = await apiFetch(`/spine/v1/fin/admin/parameter-sets/${selectedSetId}`);
      if (data && !data._error) {
        setSelectedSet(data);
      }
    } catch (err) {
      console.error('Error fetching set details:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSetId]);

  // Fetch audit log
  const fetchAuditLog = useCallback(async () => {
    try {
      const url = selectedSetId 
        ? `${API_URL}/api/spine/v1/fin/admin/audit-log?param_set_id=${selectedSetId}`
        : `${API_URL}/api/spine/v1/fin/admin/audit-log`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setAuditLog(data);
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
    }
  }, [selectedSetId]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await apiFetch(`/spine/v1/fin/admin/stats`);
      if (data && !data._error) {
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchParameterSets();
    fetchStats();
  }, [fetchParameterSets, fetchStats]);

  useEffect(() => {
    fetchSetDetails();
    fetchAuditLog();
  }, [selectedSetId, fetchSetDetails, fetchAuditLog]);

  // Create new parameter set
  const handleCreateSet = async (formData) => {
    try {
      const data = await apiFetch(`/spine/v1/fin/admin/parameter-sets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (data && !data._error) {
        setShowCreateModal(false);
        fetchParameterSets();
        setSelectedSetId(data.id);
      }
    } catch (err) {
      console.error('Error creating parameter set:', err);
    }
  };

  // Update parameter set status
  const handleStatusChange = async (newStatus) => {
    if (!selectedSetId) return;
    
    try {
      const response = await apiFetch(`/spine/v1/fin/admin/parameter-sets/${selectedSetId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_status: newStatus, updated_by: 'admin' })
      });
      if (response && !response._error) {
        fetchParameterSets();
        fetchSetDetails();
        fetchAuditLog();
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  // Add/Update parameter
  const handleSaveParameter = async (type, data) => {
    if (!selectedSetId) return;
    
    const endpoints = {
      jurisdiction: 'jurisdictions',
      penalty: 'penalties',
      claim: 'claims',
      unitcost: 'unit-costs',
      sdc: 'substitution-multipliers'
    };
    
    try {
      const response = await apiFetch(`/spine/v1/fin/admin/parameter-sets/${selectedSetId}/${endpoints[type]}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (response && !response._error) {
        setShowEditModal(false);
        setEditingItem(null);
        fetchSetDetails();
        fetchAuditLog();
      }
    } catch (err) {
      console.error('Error saving parameter:', err);
    }
  };

  // Seed defaults
  const handleSeedDefaults = async () => {
    try {
      const response = await apiFetch(`/spine/v1/fin/admin/seed-defaults`, {
        method: 'POST'
      });
      if (response && !response._error) {
        fetchParameterSets();
        fetchStats();
      }
    } catch (err) {
      console.error('Error seeding defaults:', err);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-EU', { 
      style: 'currency', 
      currency: 'EUR',
      maximumFractionDigits: 0 
    }).format(value);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const currentSetStatus = selectedSet?.parameter_set?.status || 'DRAFT';
  const canEdit = currentSetStatus === 'DRAFT';

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6" data-testid="financial-params-admin">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-500/10 rounded-xl border border-purple-500/30">
            <Database className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="font-outfit text-2xl font-bold text-white">Financial Parameters</h1>
            <p className="text-sm text-[#6B6B6B]">Manage versioned parameters for the Financial Exposure Engine</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button 
            onClick={handleSeedDefaults}
            className="px-3 py-2 bg-[#1F1F1F] text-[#A0A0A0] rounded-lg text-sm hover:bg-[#2F2F2F] border border-[#2F2F2F]"
          >
            <RefreshCw className="w-4 h-4 inline mr-2" />
            Seed Defaults
          </button>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm flex items-center gap-2"
            data-testid="create-param-set-btn"
          >
            <Plus className="w-4 h-4" />
            New Parameter Set
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-[#141414] rounded-xl border border-[#1F1F1F]">
          <div className="flex items-center gap-3 mb-2">
            <Layers className="w-5 h-5 text-purple-400" />
            <span className="text-sm text-[#6B6B6B]">Total Sets</span>
          </div>
          <p className="text-2xl font-bold">{stats.total_parameter_sets || 0}</p>
        </div>
        <div className="p-4 bg-[#141414] rounded-xl border border-[#1F1F1F]">
          <div className="flex items-center gap-3 mb-2">
            <Check className="w-5 h-5 text-emerald-400" />
            <span className="text-sm text-[#6B6B6B]">Active Set</span>
          </div>
          <p className="text-lg font-medium truncate">{stats.active_set?.name || 'None'}</p>
        </div>
        <div className="p-4 bg-[#141414] rounded-xl border border-[#1F1F1F]">
          <div className="flex items-center gap-3 mb-2">
            <Edit3 className="w-5 h-5 text-yellow-400" />
            <span className="text-sm text-[#6B6B6B]">Draft Sets</span>
          </div>
          <p className="text-2xl font-bold">{stats.by_status?.DRAFT || 0}</p>
        </div>
        <div className="p-4 bg-[#141414] rounded-xl border border-[#1F1F1F]">
          <div className="flex items-center gap-3 mb-2">
            <Archive className="w-5 h-5 text-slate-400" />
            <span className="text-sm text-[#6B6B6B]">Retired Sets</span>
          </div>
          <p className="text-2xl font-bold">{stats.by_status?.RETIRED || 0}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-6">
        {/* Parameter Sets List */}
        <div className="col-span-3 bg-[#141414] rounded-xl border border-[#1F1F1F] p-4">
          <h2 className="text-sm font-semibold text-[#A0A0A0] mb-4 uppercase tracking-wider">Parameter Sets</h2>
          <div className="space-y-2">
            {parameterSets.map(ps => (
              <button
                key={ps.id}
                onClick={() => setSelectedSetId(ps.id)}
                className={`w-full p-3 rounded-lg text-left transition-colors ${
                  selectedSetId === ps.id 
                    ? 'bg-purple-500/20 border border-purple-500/30' 
                    : 'bg-[#1F1F1F] border border-transparent hover:border-[#2F2F2F]'
                }`}
                data-testid={`param-set-${ps.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm truncate">{ps.name}</span>
                  <span className={`px-2 py-0.5 text-xs rounded border ${STATUS_CONFIG[ps.status]?.color || STATUS_CONFIG.DRAFT.color}`}>
                    {STATUS_CONFIG[ps.status]?.label || ps.status}
                  </span>
                </div>
                <div className="text-xs text-[#6B6B6B]">
                  Effective: {formatDate(ps.effective_from)}
                </div>
              </button>
            ))}
            {parameterSets.length === 0 && (
              <p className="text-sm text-[#6B6B6B] text-center py-4">No parameter sets found</p>
            )}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="col-span-9 bg-[#141414] rounded-xl border border-[#1F1F1F]">
          {selectedSet ? (
            <>
              {/* Set Header */}
              <div className="p-4 border-b border-[#1F1F1F]">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">{selectedSet.parameter_set.name}</h2>
                    <p className="text-sm text-[#6B6B6B]">
                      Created: {formatDate(selectedSet.parameter_set.created_at)} by {selectedSet.parameter_set.created_by}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 text-sm rounded border ${STATUS_CONFIG[currentSetStatus]?.color}`}>
                      {STATUS_CONFIG[currentSetStatus]?.label}
                    </span>
                    
                    {/* Workflow Actions */}
                    {currentSetStatus === 'DRAFT' && (
                      <button 
                        onClick={() => handleStatusChange('APPROVED')}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                      >
                        <Check className="w-4 h-4 inline mr-1" />
                        Approve
                      </button>
                    )}
                    {currentSetStatus === 'APPROVED' && (
                      <button 
                        onClick={() => handleStatusChange('ACTIVE')}
                        className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm"
                      >
                        <Play className="w-4 h-4 inline mr-1" />
                        Activate
                      </button>
                    )}
                    {currentSetStatus === 'ACTIVE' && (
                      <button 
                        onClick={() => handleStatusChange('RETIRED')}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                      >
                        <Archive className="w-4 h-4 inline mr-1" />
                        Retire
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-[#1F1F1F]">
                {TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors ${
                      activeTab === tab.id 
                        ? 'text-purple-400 border-purple-400' 
                        : 'text-[#6B6B6B] border-transparent hover:text-white'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="p-4 max-h-[500px] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-purple-400" />
                  </div>
                ) : (
                  <>
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-4 bg-[#1F1F1F] rounded-lg">
                            <div className="text-sm text-[#6B6B6B] mb-1">Jurisdictions</div>
                            <div className="text-2xl font-bold">{selectedSet.jurisdiction_profiles?.length || 0}</div>
                          </div>
                          <div className="p-4 bg-[#1F1F1F] rounded-lg">
                            <div className="text-sm text-[#6B6B6B] mb-1">Base Penalties</div>
                            <div className="text-2xl font-bold">{selectedSet.base_penalties?.length || 0}</div>
                          </div>
                          <div className="p-4 bg-[#1F1F1F] rounded-lg">
                            <div className="text-sm text-[#6B6B6B] mb-1">SDC Multipliers</div>
                            <div className="text-2xl font-bold">{selectedSet.substitution_multipliers?.length || 0}</div>
                          </div>
                        </div>
                        
                        {selectedSet.parameter_set.notes && (
                          <div className="p-4 bg-[#1F1F1F] rounded-lg">
                            <div className="text-sm text-[#6B6B6B] mb-1">Notes</div>
                            <p className="text-sm">{selectedSet.parameter_set.notes}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Jurisdictions Tab */}
                    {activeTab === 'jurisdictions' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium">Jurisdiction Profiles</h3>
                          {canEdit && (
                            <button 
                              onClick={() => { setEditingType('jurisdiction'); setEditingItem(null); setShowEditModal(true); }}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm"
                            >
                              <Plus className="w-4 h-4 inline mr-1" />
                              Add
                            </button>
                          )}
                        </div>
                        <table className="w-full text-sm">
                          <thead className="text-[#6B6B6B] border-b border-[#2F2F2F]">
                            <tr>
                              <th className="text-left py-2">Jurisdiction</th>
                              <th className="text-right py-2">Enforcement</th>
                              <th className="text-right py-2">Penalty Mult</th>
                              <th className="text-right py-2">Claim Mult</th>
                              <th className="text-right py-2">Currency</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSet.jurisdiction_profiles?.map(jp => (
                              <tr key={jp.jurisdiction_code} className="border-b border-[#1F1F1F] hover:bg-[#1F1F1F]">
                                <td className="py-3 font-medium">{jp.jurisdiction_code}</td>
                                <td className="py-3 text-right">{jp.enforcement_intensity?.toFixed(2)}</td>
                                <td className="py-3 text-right">{jp.penalty_multiplier?.toFixed(2)}x</td>
                                <td className="py-3 text-right">{jp.claim_multiplier?.toFixed(2)}x</td>
                                <td className="py-3 text-right">{jp.currency}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Base Penalties Tab */}
                    {activeTab === 'penalties' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium">Base Penalties by Framework/Article</h3>
                          {canEdit && (
                            <button 
                              onClick={() => { setEditingType('penalty'); setEditingItem(null); setShowEditModal(true); }}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm"
                            >
                              <Plus className="w-4 h-4 inline mr-1" />
                              Add
                            </button>
                          )}
                        </div>
                        <table className="w-full text-sm">
                          <thead className="text-[#6B6B6B] border-b border-[#2F2F2F]">
                            <tr>
                              <th className="text-left py-2">Framework</th>
                              <th className="text-left py-2">Article</th>
                              <th className="text-right py-2">Base Penalty</th>
                              <th className="text-left py-2">Band</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSet.base_penalties?.map((bp, idx) => (
                              <tr key={idx} className="border-b border-[#1F1F1F] hover:bg-[#1F1F1F]">
                                <td className="py-3 font-medium">{bp.framework}</td>
                                <td className="py-3">{bp.article_ref}</td>
                                <td className="py-3 text-right font-mono">{formatCurrency(bp.base_penalty_amount)}</td>
                                <td className="py-3">
                                  <span className={`px-2 py-0.5 text-xs rounded ${
                                    bp.penalty_band === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                                    bp.penalty_band === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-green-500/20 text-green-400'
                                  }`}>
                                    {bp.penalty_band}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Claim Costs Tab */}
                    {activeTab === 'claims' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium">Base Claim Costs by Impact Type</h3>
                          {canEdit && (
                            <button 
                              onClick={() => { setEditingType('claim'); setEditingItem(null); setShowEditModal(true); }}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm"
                            >
                              <Plus className="w-4 h-4 inline mr-1" />
                              Add
                            </button>
                          )}
                        </div>
                        <table className="w-full text-sm">
                          <thead className="text-[#6B6B6B] border-b border-[#2F2F2F]">
                            <tr>
                              <th className="text-left py-2">Impact Type</th>
                              <th className="text-left py-2">Jurisdiction</th>
                              <th className="text-right py-2">Base Claim Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSet.base_claim_costs?.map((cc, idx) => (
                              <tr key={idx} className="border-b border-[#1F1F1F] hover:bg-[#1F1F1F]">
                                <td className="py-3 font-medium">{cc.impact_type}</td>
                                <td className="py-3">{cc.jurisdiction_code}</td>
                                <td className="py-3 text-right font-mono">{formatCurrency(cc.base_claim_amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Unit Costs Tab */}
                    {activeTab === 'unitcosts' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium">Remediation Unit Costs</h3>
                          {canEdit && (
                            <button 
                              onClick={() => { setEditingType('unitcost'); setEditingItem(null); setShowEditModal(true); }}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm"
                            >
                              <Plus className="w-4 h-4 inline mr-1" />
                              Add
                            </button>
                          )}
                        </div>
                        <table className="w-full text-sm">
                          <thead className="text-[#6B6B6B] border-b border-[#2F2F2F]">
                            <tr>
                              <th className="text-left py-2">Action Type</th>
                              <th className="text-left py-2">Jurisdiction</th>
                              <th className="text-right py-2">Unit Cost</th>
                              <th className="text-left py-2">Unit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSet.unit_costs?.map((uc, idx) => (
                              <tr key={idx} className="border-b border-[#1F1F1F] hover:bg-[#1F1F1F]">
                                <td className="py-3 font-medium">{uc.action_type}</td>
                                <td className="py-3">{uc.jurisdiction_code}</td>
                                <td className="py-3 text-right font-mono">{formatCurrency(uc.unit_cost_amount)}</td>
                                <td className="py-3 text-[#6B6B6B]">{uc.unit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* SDC Multipliers Tab */}
                    {activeTab === 'sdc' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="font-medium">Supplier Disruption Cost (SDC) Multipliers</h3>
                            <p className="text-sm text-[#6B6B6B]">% of revenue at risk to substitute supplier by category</p>
                          </div>
                          {canEdit && (
                            <button 
                              onClick={() => { setEditingType('sdc'); setEditingItem(null); setShowEditModal(true); }}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm"
                            >
                              <Plus className="w-4 h-4 inline mr-1" />
                              Add
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          {selectedSet.substitution_multipliers?.map((sm, idx) => (
                            <div key={idx} className="p-4 bg-[#1F1F1F] rounded-lg flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Truck className="w-5 h-5 text-purple-400" />
                                <span className="font-medium">{sm.supplier_category}</span>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold text-purple-400">{(sm.multiplier * 100).toFixed(0)}%</div>
                                <div className="text-xs text-[#6B6B6B]">substitution cost</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Audit Trail Tab */}
                    {activeTab === 'audit' && (
                      <div>
                        <h3 className="font-medium mb-4">Change History</h3>
                        <div className="space-y-2">
                          {auditLog.map((log, idx) => (
                            <div key={idx} className="p-3 bg-[#1F1F1F] rounded-lg flex items-start gap-3">
                              <History className="w-4 h-4 text-[#6B6B6B] mt-1" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 text-sm">
                                  <span className={`px-2 py-0.5 text-xs rounded ${
                                    log.change_type === 'CREATE' ? 'bg-green-500/20 text-green-400' :
                                    log.change_type === 'UPDATE' ? 'bg-blue-500/20 text-blue-400' :
                                    log.change_type === 'STATUS_CHANGE' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-red-500/20 text-red-400'
                                  }`}>
                                    {log.change_type}
                                  </span>
                                  <span className="font-medium">{log.entity_type}</span>
                                  <span className="text-[#6B6B6B]">•</span>
                                  <span className="text-[#6B6B6B]">{log.entity_id}</span>
                                </div>
                                <div className="text-xs text-[#6B6B6B] mt-1">
                                  {formatDate(log.changed_at)} by {log.changed_by}
                                </div>
                              </div>
                            </div>
                          ))}
                          {auditLog.length === 0 && (
                            <p className="text-sm text-[#6B6B6B] text-center py-4">No changes recorded</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <Database className="w-12 h-12 text-[#2F2F2F] mx-auto mb-4" />
                <p className="text-[#6B6B6B]">Select a parameter set to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateParameterSetModal 
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateSet}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditParameterModal
          type={editingType}
          item={editingItem}
          onClose={() => { setShowEditModal(false); setEditingItem(null); }}
          onSave={(data) => handleSaveParameter(editingType, data)}
        />
      )}
    </div>
  );
};

// Create Parameter Set Modal
const CreateParameterSetModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: '',
    notes: ''
  });

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-[#141414] rounded-xl border border-[#1F1F1F] w-[500px] max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between">
          <h2 className="text-lg font-semibold">Create Parameter Set</h2>
          <button onClick={onClose} className="p-1 hover:bg-[#2F2F2F] rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm text-[#6B6B6B] mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
              placeholder="e.g., FIN_PARAMS_2026_Q1"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#6B6B6B] mb-1">Effective From *</label>
              <input
                type="date"
                value={formData.effective_from}
                onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#6B6B6B] mb-1">Effective To</label>
              <input
                type="date"
                value={formData.effective_to}
                onChange={(e) => setFormData({ ...formData, effective_to: e.target.value })}
                className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-[#6B6B6B] mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white h-20"
              placeholder="Description or rationale for this parameter set"
            />
          </div>
        </div>
        <div className="p-4 border-t border-[#1F1F1F] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-[#A0A0A0] hover:text-white">
            Cancel
          </button>
          <button 
            onClick={() => onSave(formData)}
            disabled={!formData.name || !formData.effective_from}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

// Edit Parameter Modal (simplified)
const EditParameterModal = ({ type, item, onClose, onSave }) => {
  const [formData, setFormData] = useState(item || getDefaultFormData(type));

  function getDefaultFormData(type) {
    switch(type) {
      case 'jurisdiction':
        return { jurisdiction_code: '', enforcement_intensity: 1.0, penalty_multiplier: 1.0, claim_multiplier: 1.0, currency: 'EUR' };
      case 'penalty':
        return { framework: 'CSDDD', article_ref: '', jurisdiction_code: 'EU', base_penalty_amount: 500000, penalty_band: 'MEDIUM' };
      case 'claim':
        return { impact_type: '', jurisdiction_code: 'EU', base_claim_amount: 100000 };
      case 'unitcost':
        return { action_type: '', jurisdiction_code: 'EU', unit_cost_amount: 25000, unit: 'PER_ACTION' };
      case 'sdc':
        return { supplier_category: '', multiplier: 0.20 };
      default:
        return {};
    }
  }

  const getTitle = () => {
    const titles = {
      jurisdiction: 'Jurisdiction Profile',
      penalty: 'Base Penalty',
      claim: 'Claim Cost',
      unitcost: 'Unit Cost',
      sdc: 'SDC Multiplier'
    };
    return `${item ? 'Edit' : 'Add'} ${titles[type] || 'Parameter'}`;
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-[#141414] rounded-xl border border-[#1F1F1F] w-[500px] max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between">
          <h2 className="text-lg font-semibold">{getTitle()}</h2>
          <button onClick={onClose} className="p-1 hover:bg-[#2F2F2F] rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {type === 'sdc' && (
            <>
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1">Supplier Category *</label>
                <select
                  value={formData.supplier_category}
                  onChange={(e) => setFormData({ ...formData, supplier_category: e.target.value })}
                  className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
                >
                  <option value="">Select category</option>
                  <option value="TEXTILES">Textiles</option>
                  <option value="ELECTRONICS">Electronics</option>
                  <option value="CHEMICALS">Chemicals</option>
                  <option value="METALS">Metals</option>
                  <option value="AGRICULTURE">Agriculture</option>
                  <option value="ENERGY">Energy</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1">Substitution Cost Multiplier (0-1)</label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={formData.multiplier}
                  onChange={(e) => setFormData({ ...formData, multiplier: parseFloat(e.target.value) })}
                  className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
                />
                <p className="text-xs text-[#6B6B6B] mt-1">% of revenue at risk required to substitute supplier</p>
              </div>
            </>
          )}
          
          {type === 'jurisdiction' && (
            <>
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1">Jurisdiction Code *</label>
                <input
                  type="text"
                  value={formData.jurisdiction_code}
                  onChange={(e) => setFormData({ ...formData, jurisdiction_code: e.target.value.toUpperCase() })}
                  className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
                  placeholder="e.g., DE, FR, EU"
                />
              </div>
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1">Enforcement Intensity (0-2)</label>
                <input
                  type="number"
                  min="0"
                  max="2"
                  step="0.1"
                  value={formData.enforcement_intensity}
                  onChange={(e) => setFormData({ ...formData, enforcement_intensity: parseFloat(e.target.value) })}
                  className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#6B6B6B] mb-1">Penalty Multiplier</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={formData.penalty_multiplier}
                    onChange={(e) => setFormData({ ...formData, penalty_multiplier: parseFloat(e.target.value) })}
                    className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#6B6B6B] mb-1">Claim Multiplier</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={formData.claim_multiplier}
                    onChange={(e) => setFormData({ ...formData, claim_multiplier: parseFloat(e.target.value) })}
                    className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
                  />
                </div>
              </div>
            </>
          )}

          {type === 'penalty' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#6B6B6B] mb-1">Framework *</label>
                  <select
                    value={formData.framework}
                    onChange={(e) => setFormData({ ...formData, framework: e.target.value })}
                    className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
                  >
                    <option value="CSDDD">CSDDD</option>
                    <option value="LkSG">LkSG</option>
                    <option value="EUDR">EUDR</option>
                    <option value="ESRS">ESRS</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[#6B6B6B] mb-1">Article Reference *</label>
                  <input
                    type="text"
                    value={formData.article_ref}
                    onChange={(e) => setFormData({ ...formData, article_ref: e.target.value })}
                    className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
                    placeholder="e.g., Art. 9"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1">Base Penalty Amount (€)</label>
                <input
                  type="number"
                  value={formData.base_penalty_amount}
                  onChange={(e) => setFormData({ ...formData, base_penalty_amount: parseInt(e.target.value) })}
                  className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1">Penalty Band</label>
                <select
                  value={formData.penalty_band}
                  onChange={(e) => setFormData({ ...formData, penalty_band: e.target.value })}
                  className="w-full p-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded text-white"
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                </select>
              </div>
            </>
          )}
        </div>
        <div className="p-4 border-t border-[#1F1F1F] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-[#A0A0A0] hover:text-white">
            Cancel
          </button>
          <button 
            onClick={() => onSave(formData)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinancialParametersAdmin;
